const Cookies = require("js-cookie");

// Capa de acceso al microservicio `lucy` (chat + cuotas + auditoria).
// Mismo esquema de auth que authService.js: JWT de Cognito en cookie.
// CommonJS puro (require + module.exports) como el resto de src/helpers:
// mezclar `import` con `module.exports` rompe el build de CRA.

const BASE = process.env.REACT_APP_LUCY;

if (!BASE) {
  // CRA reemplaza process.env.REACT_APP_LUCY por `undefined` si no está en el
  // .env con el que se compiló. Sin esto, las llamadas irían a "undefined/...".
  // eslint-disable-next-line no-console
  console.error(
    "[ia] REACT_APP_LUCY no está definido en el build. Agregá " +
      "REACT_APP_LUCY=<url del backend de la IA institucional> al .env y reconstruí."
  );
}

const requireBase = () => {
  if (!BASE) {
    throw {
      mensaje: "La IA institucional no está configurada (falta REACT_APP_LUCY en el build).",
      codigo: 0,
      status: 0,
    };
  }
};

const authHeader = () => {
  const raw = Cookies.get(process.env.REACT_APP_COOKIE_KEY);
  const jwt = raw ? JSON.parse(raw) : null;
  if (!jwt || !jwt.TokenType || !jwt.accessToken) {
    window.location.href = process.env.REACT_APP_REDIRECT_ENDPOINT;
    throw { mensaje: "ERROR: sesion no valida", codigo: 0, status: 401 };
  }
  return `${jwt.TokenType} ${jwt.accessToken}`;
};

const request = async (method, api, body) => {
  requireBase();
  const res = await fetch(BASE + api, {
    method,
    cache: "no-cache",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: authHeader(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    window.location.href = process.env.REACT_APP_REDIRECT_ENDPOINT;
    throw { mensaje: "ERROR: no autorizado", codigo: 0, status: 401 };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw {
      mensaje: "ERROR: " + (data.log || res.statusText),
      codigo: 0,
      status: res.status,
      data,
    };
  }
  return { mensaje: "Ok", codigo: 1, data };
};

const get = (api) => request("GET", api);
const post = (api, body) => request("POST", api, body || {});
const patch = (api, body) => request("PATCH", api, body || {});
const del = (api) => request("DELETE", api);

// ---- Conversaciones -------------------------------------------------------
const listConversations = (params = {}) =>
  get(`/conversations${qs(params)}`);
const createConversation = (title) => post(`/conversations`, { title });
const getConversation = (id, params = {}) =>
  get(`/conversations/${id}${qs(params)}`);
const renameConversation = (id, title) => patch(`/conversations/${id}`, { title });
const archiveConversation = (id) => patch(`/conversations/${id}`, { status: "archived" });
const deleteConversation = (id) => del(`/conversations/${id}`);

// ---- Adjuntos ----------------------------------------------------------
const presignAttachment = ({ name, type, size }) =>
  post(`/attachments/presign`, { name, type, size });
const finalizeAttachment = ({ key, name, type }) =>
  post(`/attachments/finalize`, { key, name, type });

const uploadAttachment = async (file) => {
  const { data } = await presignAttachment({
    name: file.name,
    type: file.type,
    size: file.size,
  });
  await fetch(data.response.url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  await finalizeAttachment({ key: data.response.key, name: file.name, type: file.type });
  return { key: data.response.key, name: file.name, type: file.type };
};

// ---- Envio de mensaje (streaming SSE sobre fetch) -----------------------
// EventSource no permite cabecera Authorization, por eso se usa fetch + reader.
const streamMessage = async (
  conversationId,
  { text, attachments },
  { onDelta, onUsage, onDone, onError, signal } = {}
) => {
  try {
    requireBase();
  } catch (e) {
    onError && onError({ message: e.mensaje });
    return;
  }
  let res;
  try {
    res = await fetch(BASE + `/conversations/${conversationId}/messages`, {
      method: "POST",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: authHeader(),
        Accept: "text/event-stream",
      },
      body: JSON.stringify({ text, attachments }),
      signal,
    });
  } catch (err) {
    if (err && err.name === "AbortError") return;
    onError && onError({ message: String(err) });
    return;
  }

  if (res.status === 402) {
    const data = await res.json().catch(() => ({}));
    onError && onError({ code: "quota_exhausted", ...data });
    return;
  }
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({}));
    onError && onError({ message: data.log || res.statusText, status: res.status });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const evLine = frame.split("\n").find((l) => l.startsWith("event:"));
        const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        const event = evLine ? evLine.slice(6).trim() : "message";
        let payload = {};
        try {
          payload = JSON.parse(dataLine.slice(5).trim());
        } catch (e) {
          continue;
        }
        if (event === "delta") onDelta && onDelta(payload.text || "");
        else if (event === "usage") onUsage && onUsage(payload);
        else if (event === "done") onDone && onDone(payload);
        else if (event === "error") onError && onError(payload);
      }
    }
  } catch (err) {
    if (!(err && err.name === "AbortError")) onError && onError({ message: String(err) });
  }
};

// ---- Solicitudes de tokens (usuario) ---------------------------------
const requestMoreTokens = (message) => post(`/token-requests`, { message });
const myTokenRequests = () => get(`/token-requests/mine`);

// ---- Mantenimiento de usuarios: admin ------------------------------
const adminListTokenRequests = (params = {}) => get(`/token-requests${qs(params)}`);
const adminResolveTokenRequest = (userId, requestedAt, payload) =>
  post(`/token-requests/${userId}/${encodeURIComponent(requestedAt)}/resolve`, payload);

const getUserQuota = (userId) => get(`/users/${userId}/quota`);
const extendUserQuota = (userId, payload) => post(`/users/${userId}/quota/extend`, payload);
const openNewWindow = (userId, payload) => post(`/users/${userId}/quota/new-window`, payload);
const setUserDefaultLimit = (userId, limit) =>
  post(`/users/${userId}/quota/default`, { limit });

const getUserStats = (userId) => get(`/users/${userId}/stats`);
const getUserConversations = (userId, params = {}) =>
  get(`/users/${userId}/conversations${qs(params)}`);

// ---- Log de auditoria -------------------------------------------------
const getUserAuditLog = (userId, filters = {}) =>
  get(`/users/${userId}/audit${qs(filters)}`);

const downloadUserAuditCsv = async (userId, filters = {}) => {
  requireBase();
  const res = await fetch(BASE + `/users/${userId}/audit/export.csv${qs(filters)}`, {
    headers: { Authorization: authHeader() },
  });
  if (!res.ok) throw { mensaje: "ERROR al exportar", status: res.status };
  return res.blob();
};

const getGlobalAuditLog = (filters = {}) => get(`/audit${qs(filters)}`);
const getGlobalStats = () => get(`/stats`);
const getLoginMetrics = (params = {}) => get(`/stats/logins${qs(params)}`);

// ---- util --------------------------------------------------------------
function qs(obj) {
  const entries = Object.entries(obj || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (!entries.length) return "";
  return (
    "?" +
    entries
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&")
  );
}

module.exports = {
  listConversations,
  createConversation,
  getConversation,
  renameConversation,
  archiveConversation,
  deleteConversation,
  presignAttachment,
  finalizeAttachment,
  uploadAttachment,
  streamMessage,
  requestMoreTokens,
  myTokenRequests,
  adminListTokenRequests,
  adminResolveTokenRequest,
  getUserQuota,
  extendUserQuota,
  openNewWindow,
  setUserDefaultLimit,
  getUserStats,
  getUserConversations,
  getUserAuditLog,
  downloadUserAuditCsv,
  getGlobalAuditLog,
  getGlobalStats,
  getLoginMetrics,
};
